import { Check, CloudOff, RefreshCw, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOnlineStatus } from "@/offline/online-status";
import { SYNCED_CONFIRM_DURATION_MS } from "@/hooks/use-just-synced";

export type RecordSyncState = "pending" | "failed" | "synced";

interface RecordSyncBadgeProps {
  state: RecordSyncState;
  /**
   * True when the badge describes photos being **appended** to an already
   * synced record rather than the record's own create. Only changes the failed
   * wording: an append-failed surface opens on tap (retry lives on the failed
   * photo in the viewer), so it must not promise tap-to-retry.
   */
  isAppend?: boolean;
  /** Positioning/extra classes from the host surface. */
  className?: string;
}

/**
 * The small sync pill a record surface shows while its create (or its appended
 * photos) are still in the outbox — shared by the grid tile
 * ([[photo-grid-item.spec]]) and the list row ([[photo-list-item.spec]]) so the
 * two can never drift.
 *
 * The pending badge is connectivity-aware. Background sync only drains the
 * outbox while online — see [[offline-record-outbox]] § Background sync — so
 * offline it reads "Queued" with a static cloud glyph rather than lying with a
 * spinner. The status flips on its own when connectivity returns
 * (`useOnlineStatus` is a `useSyncExternalStore` over the online/offline
 * events).
 */
export function RecordSyncBadge({
  state,
  isAppend = false,
  className,
}: RecordSyncBadgeProps) {
  const isOnline = useOnlineStatus();

  if (state === "pending") {
    return (
      <div
        className={cn("pointer-events-none z-10", className)}
        data-testid={isOnline ? "outbox-badge-syncing" : "outbox-badge-queued"}
      >
        <span className="inline-flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {isOnline ? (
            <>
              <RefreshCw className="h-2.5 w-2.5 animate-spin" />
              Syncing
            </>
          ) : (
            <>
              <CloudOff className="h-2.5 w-2.5" />
              Queued
            </>
          )}
        </span>
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div
        className={cn("pointer-events-none z-10", className)}
        data-testid="outbox-badge-failed"
      >
        <span className="inline-flex items-center gap-1 rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-medium text-destructive-foreground">
          <TriangleAlert className="h-2.5 w-2.5" />
          {isAppend ? "Upload failed" : "Tap to retry"}
        </span>
      </div>
    );
  }

  // Brief post-sync confirmation. The `synced-confirm` keyframe fades the badge
  // in, holds, and fades it out across exactly SYNCED_CONFIRM_DURATION_MS — the
  // badge unmounts at opacity 0.
  return (
    <div
      className={cn("pointer-events-none z-10", className)}
      data-testid="outbox-badge-synced"
      style={{
        animation: `synced-confirm ${SYNCED_CONFIRM_DURATION_MS}ms ease-out forwards`,
      }}
    >
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
        <Check className="h-2.5 w-2.5" />
        Synced
      </span>
    </div>
  );
}
